import React from "react";
import {Box, Colors, Container, FormControl, IconButton, IconTrash, Text} from "@bounce/shared/ui";
import {MenuItem, Select, TextField} from "../../ui/components";
import {ParamSchema} from "../../apis/requests/segments/types";
import {TriggerRule} from "../../apis/requests/triggers/types";
import EnumValueInput from "../segments/segment-form/value-inputs/EnumValueInput";
import MultiTextValueInput from "../segments/segment-form/value-inputs/MultiTextValueInput";
import NumericValueInput from "../segments/segment-form/value-inputs/NumericValueInput";
import TextValueInput from "../segments/segment-form/value-inputs/TextValueInput";
import {MULTI_VALUE_OPERATORS} from "../segments/segment-form/constants";
import {defaultValueForCondition, findParam} from "../segments/segment-form/helpers";
import {NO_VALUE_OPERATORS, OPERATOR_LABELS, TRIGGER_MONEY_PARAMETERS} from "./triggerDisplay";

type Props = {
  rule: TriggerRule;
  parameters: ParamSchema[];
  onChange: (rule: TriggerRule) => void;
  onRemove: () => void;
  showLabels: boolean;
};

const ColumnLabel = ({children, hidden = false}: {children: string; hidden?: boolean}) => (
  <Text size={12} sx={{color: Colors.grey._700, mb: 0.5, visibility: hidden ? "hidden" : "visible"}}>
    {children}
  </Text>
);

const TriggerRuleRow: React.FC<Props> = ({rule, parameters, onChange, onRemove, showLabels}) => {
  const param = findParam(parameters, rule.field);
  const multi = MULTI_VALUE_OPERATORS.has(rule.operator);

  const onFieldChange = (field: string) => {
    const next = findParam(parameters, field);
    const operator = (next?.operators[0] ?? "equals") as TriggerRule["operator"];
    onChange({field, operator, value: defaultValueForCondition(next, operator) as TriggerRule["value"]});
  };

  const onOperatorChange = (operator: TriggerRule["operator"]) =>
    onChange({...rule, operator, value: defaultValueForCondition(param, operator) as TriggerRule["value"]});

  const onValueChange = (value: unknown) => onChange({...rule, value: value as TriggerRule["value"]});

  const renderValue = () => {
    if (NO_VALUE_OPERATORS.has(rule.operator)) {
      return (
        <Box sx={{flex: 2}}>
          {showLabels && <ColumnLabel>Value</ColumnLabel>}
          <TextField size="small" fullWidth disabled placeholder="Not used" />
        </Box>
      );
    }
    if (param?.options?.length) {
      return (
        <EnumValueInput
          value={rule.value}
          onChange={onValueChange}
          options={param.options}
          multi={multi}
          showLabel={showLabels}
        />
      );
    }
    if (multi) {
      return <MultiTextValueInput value={rule.value} onChange={onValueChange} showLabel={showLabels} />;
    }
    if (param?.kind === "numeric") {
      return (
        <NumericValueInput
          value={rule.value}
          onChange={onValueChange}
          isFraction={false}
          isBalance={TRIGGER_MONEY_PARAMETERS.has(rule.field)}
          showLabel={showLabels}
        />
      );
    }
    return <TextValueInput value={rule.value} onChange={onValueChange} showLabel={showLabels} />;
  };

  return (
    <Container row fullWidth sx={{gap: 1, alignItems: "flex-start", flexWrap: "nowrap"}}>
      <Box sx={{width: 220, flexShrink: 0}}>
        {showLabels && <ColumnLabel>Field</ColumnLabel>}
        <FormControl size="small" fullWidth>
          <Select
            fullWidth
            displayEmpty
            value={rule.field}
            onChange={(e) => onFieldChange(e.target.value as string)}
            renderValue={(selected) => (
              <Box component="span" sx={{fontFamily: "monospace", fontSize: 13}}>
                {(selected as string) || "Pick a field"}
              </Box>
            )}
            MenuProps={{PaperProps: {style: {maxHeight: 400}}}}
          >
            {parameters.map((p) => (
              <MenuItem key={p.parameter} value={p.parameter}>
                <Box component="span" sx={{fontFamily: "monospace", fontSize: 13}}>
                  {p.parameter}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{width: 140, flexShrink: 0}}>
        {showLabels && <ColumnLabel>Operator</ColumnLabel>}
        <FormControl size="small" fullWidth disabled={!param}>
          <Select
            fullWidth
            value={rule.operator}
            onChange={(e) => onOperatorChange(e.target.value as TriggerRule["operator"])}
          >
            {(param?.operators ?? [rule.operator]).map((op) => (
              <MenuItem key={op} value={op}>
                {OPERATOR_LABELS[op as TriggerRule["operator"]] ?? op}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {renderValue()}
      <Box>
        {showLabels && <ColumnLabel hidden>-</ColumnLabel>}
        <IconButton
          size="small"
          onClick={onRemove}
          aria-label="Remove rule"
          sx={{
            border: `1px solid ${Colors.tertiary._300}`,
            borderRadius: "8px",
            width: 36,
            height: 36,
            color: Colors.grey._700,
            "& svg path": {stroke: "currentColor"},
            "&:hover": {backgroundColor: Colors.red._100, borderColor: Colors.red._300, color: Colors.red._700},
          }}
        >
          <Box component={IconTrash} sx={{width: 16, height: 16}} />
        </IconButton>
      </Box>
    </Container>
  );
};

export default TriggerRuleRow;
